import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { UnpaidPaymentsAdapterService, UnifiedProductOption, UnifiedUnpaidFilters, UnifiedUnpaidPayment } from '../../unpaid-payments-adapter.service';
import { ProductConfigService } from '../../../../../core/services/product-config.service';
import { UnpaidPartnerTileSummaryService } from '../../unpaid-partner-tile-summary.service';
import { TableHeader } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import { DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { UnpaidPaymentsCalculationService } from '../../unpaid-payments-calculation.service';
import { UnpaidPaymentsModalService } from '../../unpaid-payments-modal.service';
import {
  UnpaidPaymentsFundingSourceId,
  UnpaidPaymentsFundingSourceOption,
} from '../../unpaid-payments-modal.types';
import {
  FmListRouteDefaults,
  FmListRouteState,
  buildFmListQueryParams,
  getDefaultIsDummy,
  parseFmListRouteState,
} from '../../unpaid-payments-route-query.util';
import { getDefaultFmFilter } from '../../unpaid-payments-hub-products.config';
import { UNPAID_PAYMENTS_BULK_SELECTION_STYLES } from '../../unpaid-payments-bulk-selection.styles';
import { UnpaidPaymentsIsDummyService } from '../../unpaid-payments-is-dummy.service';
import { UnpaidPaymentsAccessService } from '../../unpaid-payments-access.service';
import { UnpaidPaymentsHubOverviewService } from '../../unpaid-payments-hub-overview.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { PaginatedTableComponent } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import {
  DropdownComponent,
  DropdownTriggerDirective,
} from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToggleComponent } from '../../../../../shared/components/ui/toggle/toggle.component';
import { UnpaidPartnerOverviewCardsComponent } from '../../components/unpaid-partner-overview-cards.component';
import { UnpaidPaymentsIsDummyToggleComponent } from '../../components/unpaid-payments-is-dummy-toggle/unpaid-payments-is-dummy-toggle.component';

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-unpaid-payments-fm',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconComponentV2,
    PaginatedTableComponent,
    DropdownComponent,
    DropdownTriggerDirective,
    ButtonComponent,
    ToggleComponent,
    UnpaidPartnerOverviewCardsComponent,
    UnpaidPaymentsIsDummyToggleComponent,
  ],
  templateUrl: './unpaid-payments-fm.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [UNPAID_PAYMENTS_BULK_SELECTION_STYLES],
})
export class UnpaidPaymentsFmComponent implements OnInit {
  readonly supportedFilters = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const;
  /** FM filters that support mark/remove urgent + notes in the row more menu. */
  private readonly urgentActionFilters = [9, 14] as const;
  readonly productKey = 'fm';
  readonly loading = signal(false);
  readonly paying = signal(false);
  readonly adminLoading = signal(false);
  readonly markingUrgent = signal(false);
  readonly tableBusy = computed(() => this.loading() || this.paying() || this.adminLoading() || this.markingUrgent());
  readonly tableBusyMessage = computed(() => {
    if (this.paying()) return 'Processing payments...';
    if (this.adminLoading()) return 'Processing...';
    if (this.markingUrgent()) return 'Processing...';
    return 'Loading unpaid records...';
  });
  readonly payAllButtonLabel = computed(() =>
    this.calculationService.resolveUnpaidPayAllLabel({
      fmFilter: this.fmFilter(),
      fmStatusFilter: this.fmStatusFilter(),
      fmPartnerStatus: this.fmPartnerStatus(),
    })
  );
  readonly exporting = signal(false);
  readonly urgentNotes = signal<any[]>([]);
  readonly payments = signal<UnifiedUnpaidPayment[]>([]);
  readonly search = signal('');
  readonly searchApplied = signal(false);
  readonly fmFilter = signal<number>(getDefaultFmFilter('fm'));
  readonly fmStatusFilter = signal('0');
  readonly fmPartnerStatus = signal('0');
  readonly challengeType = signal('0');
  readonly orpFilter = signal(-1);
  readonly instantClaim = signal(-1);
  readonly vipType = signal(0);
  readonly urgentOnly = signal(false);
  readonly availableBalance = signal(0);
  readonly pendingBalance = signal(0);
  readonly totalAmount = signal(0);
  readonly availableSources = signal<{ label: string; value: string }[]>([]);
  readonly availableSourceBalance = signal<Record<string, number>>({ card: 0, bank_account: 0 });
  readonly selectedIds = signal<Array<number | string>>([]);
  /** Filter 14: rows share contract ids — selection keyed by page-index. */
  private readonly selectedPaymentsByKey = signal<Map<string, UnifiedUnpaidPayment>>(new Map());
  private static readonly ROW_SELECTION_KEY = '_selectionKey';
  readonly page = signal(1);
  readonly totalCount = signal(0);
  readonly totalPages = signal(0);
  readonly payableCount = signal(0);
  readonly overviewCalculating = signal(false);
  readonly listAggregateIds = signal<unknown>(null);
  private payableCountRequestId = 0;
  readonly pageSize = 10;
  readonly offset = computed(() => (this.page() - 1) * this.pageSize);
  readonly fmFilterDropdownValue = computed(() => String(this.fmFilter()));
  readonly vipTypeDropdownValue = computed(() => String(this.vipType()));
  readonly orpFilterDropdownValue = computed(() => String(this.orpFilter()));
  readonly instantClaimDropdownValue = computed(() => String(this.instantClaim()));
  readonly canGoPrev = computed(() => this.page() > 1);
  readonly canGoNext = computed(() => this.page() < this.totalPages());
  private readonly paymentConfirmTitle =
    'Are you sure you want to pay the Business for this unpaid payment ?';
  readonly overview = computed(() => this.partnerTileSummary.overviewFor(this.productKey));
  readonly showUrgentOverviewAction = false;
  readonly totalSelectedAmount = computed(() =>
    this.selectedPayablePayments().reduce((sum, p) => sum + this.getPayableAmount(p), 0)
  );
  readonly selectionCount = computed(() =>
    this.usesIndexRowSelection() ? this.selectedPaymentsByKey().size : this.selectedIds().length
  );
  readonly selectedRows = computed(() => {
    if (this.usesIndexRowSelection()) {
      const keys = this.selectedPaymentsByKey();
      return this.payments().filter((p) => keys.has(this.getRowSelectionKey(p)));
    }
    return this.payments().filter((p) => {
      const id = this.paymentId(p);
      return id !== null && this.selectedIds().includes(id);
    });
  });
  readonly selectedPayablePayments = computed(() =>
    (this.usesIndexRowSelection()
      ? Array.from(this.selectedPaymentsByKey().values())
      : this.selectedRows()
    ).filter((p) => this.isRowPayable(p))
  );
  readonly selectedPayableCount = computed(() => this.selectedPayablePayments().length);
  readonly selectedBusinessesCount = computed(() => {
    const ids = new Set<string>();
    for (const row of this.selectedPayablePayments()) {
      const bizId = row?.['doctor_id'] ?? row?.['partner_id'];
      if (bizId !== undefined && bizId !== null && bizId !== '') {
        ids.add(String(bizId));
      }
    }
    return ids.size;
  });
  readonly selectedUrgentCount = computed(() =>
    this.selectedPayablePayments().filter((row) => this.isUrgent(row)).length
  );
  readonly selectedUrgentAmount = computed(() =>
    this.selectedPayablePayments()
      .filter((row) => this.isUrgent(row))
      .reduce((sum, row) => sum + this.getPayableAmount(row), 0)
  );
  readonly useIndexRowSelection = computed(() => this.fmFilter() === 14);
  readonly canSelectRowBind = (row: UnifiedUnpaidPayment) => this.canSelectRow(row);
  readonly rowSelectionIdBind = (row: UnifiedUnpaidPayment, index: number): string | number | null => {
    if (this.usesIndexRowSelection()) {
      return this.getRowSelectionKey(row) || `${this.page()}-${index}`;
    }
    return this.paymentId(row);
  };
  readonly partnerStatusDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'Both' },
    { value: '1', label: 'Active' },
    { value: '2', label: 'Inactive' },
  ];
  readonly orpDropdownOptions: DropdownOption[] = [
    { value: '-1', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ];
  readonly instantClaimDropdownOptions: DropdownOption[] = [
    { value: '-1', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ];
  readonly lendeeShareType = signal(0);
  readonly lendeePaymentStatus = signal(0);
  readonly lendeeShareTypeDraft = signal(0);
  readonly lendeePaymentStatusDraft = signal(0);
  readonly searchDraft = signal('');

  readonly lendeeShareTypeOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Business Share' },
    { value: '2', label: 'Supporter Share' },
  ];

  readonly lendeePaymentStatusOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'Paid' },
    { value: '2', label: 'Unpaid' },
  ];

  readonly isLendeeFilter = computed(() => this.fmFilter() === 15);

  readonly lendeeTableHeaders: TableHeader[] = [
    { key: 'serialNo', label: 'S No.', sortable: false, colClass: 'whitespace-nowrap w-16' },
    { key: 'payableAmount', label: 'Payable Amount', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'shareType', label: 'Share Type', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'lendeeStatus', label: 'Status', sortable: false, colClass: 'whitespace-nowrap' },
    { key: 'contractDetails', label: 'Contract Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'supporterDetails', label: 'Supporter Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'paymentDetails', label: 'Payment Details', sortable: false, colClass: 'min-w-[200px] w-px' },
    { key: 'bankAdded', label: 'Bank Added', sortable: false, colClass: 'whitespace-nowrap w-px' },
    { key: 'action', label: 'Action', sortable: false, align: 'text-left', colClass: 'whitespace-nowrap w-px' },
  ];

  readonly openActionMenuRowId = signal<string | null>(null);
  readonly openActionMenuRow = signal<UnifiedUnpaidPayment | null>(null);
  readonly openActionMenuRowIndex = signal<number | null>(null);
  readonly actionMenuPosition = signal<{ top: number; left: number } | null>(null);

  @ViewChild('actionMenuPanel') private actionMenuPanel?: ElementRef<HTMLElement>;
  private actionMenuTrigger: HTMLElement | null = null;
  private readonly actionMenuWidth = 220;
  private readonly actionMenuGap = 4;
  private readonly actionMenuViewportMargin = 8;
  private readonly actionMenuItemHeight = 40;
  private readonly actionMenuPadding = 8;
  private readonly actionMenuBottomBarReserve = 88;
  private readonly rowMenuOptionsCache = new Map<string, DropdownOption[]>();
  readonly challengeDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: '$15 Challenge' },
    { value: '2', label: '$100 Challenge' },
  ];
  readonly vipDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'VIP' },
  ];
  readonly exportDropdownOptions: DropdownOption[] = [
    { value: 'xlsx', label: 'Export XLSX', icon: 'fileText' },
    { value: 'csv', label: 'Export CSV', icon: 'fileText' },
  ];
  readonly allVisibleSelected = computed(() => {
    const rows = this.payments().filter((p) => this.canSelectRow(p));
    if (!rows.length) return false;
    if (this.usesIndexRowSelection()) {
      const selected = this.selectedPaymentsByKey();
      return rows.every((p) => selected.has(this.getRowSelectionKey(p)));
    }
    return rows.every((p) => {
      const id = this.paymentId(p);
      return id !== null && this.selectedIds().includes(id);
    });
  });
  readonly visibleUrgentRows = computed(() =>
    this.payments().filter((p) => this.isRowPayable(p) && Number(p['is_urgent']) === 1)
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly isDummyService = inject(UnpaidPaymentsIsDummyService);
  private readonly accessService = inject(UnpaidPaymentsAccessService);
  private readonly hubOverviewService = inject(UnpaidPaymentsHubOverviewService);
  private lastRouteSignature = '';

  constructor(
    private readonly adapterService: UnpaidPaymentsAdapterService,
    private readonly productConfigService: ProductConfigService,
    private readonly partnerTileSummary: UnpaidPartnerTileSummaryService,
    private readonly unpaidPaymentsModal: UnpaidPaymentsModalService,
    private readonly calculationService: UnpaidPaymentsCalculationService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const nextState = parseFmListRouteState(params, this.fmRouteDefaults);
      const signature = JSON.stringify(nextState);
      if (signature === this.lastRouteSignature) {
        return;
      }
      this.lastRouteSignature = signature;
      this.applyRouteState(nextState);
      this.fetchPayments();
    });
  }

  private get fmRouteDefaults(): FmListRouteDefaults {
    return {
      filter: getDefaultFmFilter('fm'),
      supportedFilters: this.supportedFilters,
      includeLendeeFilters: true,
      lendeeFilter: 15,
      lendeeShareType: 0,
      lendeePaymentStatus: 0,
      isDummy: getDefaultIsDummy(),
    };
  }

  onIsDummyToggle(isDummy: boolean): void {
    this.navigateWithFilters({ isDummy });
  }

  get filterOptions(): SelectOption<number>[] {
    return [
      { value: 0, label: 'Businesses' },
      { value: 1, label: 'Partners' },
      { value: 2, label: 'Paul' },
      { value: 3, label: 'Protected' },
      { value: 4, label: 'Won Disputes' },
      { value: 5, label: 'ACH Businesses' },
      { value: 6, label: 'ACH Partners' },
      { value: 7, label: 'Contract Differences' },
      { value: 8, label: 'Transfer Disabled' },
      { value: 9, label: 'Cash Adjustment' },
      { value: 10, label: 'ACH Debt Recovery' },
      { value: 11, label: 'FM Performance Financing' },
      { value: 12, label: 'Bank Not Added - Transfer Disabled' },
      { value: 13, label: 'Business Challenge' },
      { value: 14, label: 'Recurring Cashback' },
      { value: 15, label: 'Unpaid Lendee Payment' },
    ];
  }

  get filterDropdownOptions(): DropdownOption[] {
    return this.filterOptions.map((option) => ({
      value: String(option.value),
      label: option.label,
    }));
  }

  get statusDropdownOptions(): DropdownOption[] {
    return this.statusOptions.map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }

  get statusFilterLabel(): string {
    const filter = this.fmFilter();
    if (filter === 0) return 'Businesses';
    if (filter === 1 || filter === 6) return 'Share type';
    return 'Status';
  }

  get statusOptions(): SelectOption<string>[] {
    const filter = this.fmFilter();
    if (filter === 1 || filter === 6) {
      return [
        { value: '0', label: 'Both' },
        { value: '1', label: 'Share from Businesses' },
        { value: '2', label: 'Share from FM' },
      ];
    }
    if (filter === 9) {
      return [
        { value: '0', label: 'Business' },
        { value: '1', label: 'Partner' },
      ];
    }
    if (filter === 10) {
      return [
        { value: '0', label: 'All' },
        { value: '1', label: 'AR' },
        { value: '2', label: 'PP' },
      ];
    }
    if (filter === 13) {
      return [{ value: '0', label: 'All' }];
    }
    const options: SelectOption<string>[] = [
      { value: '0', label: 'Both' },
      { value: '1', label: 'Active Businesses' },
      { value: '2', label: 'Inactive Businesses' },
    ];
    if (filter === 4) {
      options.push({ value: '3', label: 'Partners' });
    }
    return options;
  }

  fetchPayments(): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.loading.set(true);
    this.adapterService
      .listPayments(this.productKey, config.productServerName, this.buildListFilters())
      .subscribe({
        next: (res: any) => {
          if (!this.accessService.evaluateResponse(this.productKey, res)) {
            this.loading.set(false);
            this.accessService.redirectToOverview(this.router, this.route);
            return;
          }
          const body = this.isLendeeFilter() && res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res;
          this.loading.set(false);
          const rawPayments = Array.isArray(body?.payments) ? body.payments
            : Array.isArray(body?.unpaidPayments) ? body.unpaidPayments
              : [];
          this.payments.set(this.annotatePaymentsWithSelectionKeys(rawPayments));
          const paginationTotal = Number(body?.pagination?.total ?? 0);
          this.totalCount.set(paginationTotal || this.calculationService.resolveTotalRecords(body));
          this.totalPages.set(
            Number(body?.pagination?.total_pages ?? 0) || this.calculationService.resolveApiPageCount(body, this.pageSize)
          );
          const clampedPage = this.calculationService.clampPageToApiTotal(this.page(), this.totalPages());
          if (clampedPage != null) {
            this.navigateWithFilters({ page: clampedPage });
            return;
          }
          this.availableBalance.set(Number(body?.available_balance ?? res?.available_balance ?? 0));
          this.pendingBalance.set(Number(body?.pending_balance ?? res?.pending_balance ?? 0));
          this.totalAmount.set(Number(body?.total_amount ?? res?.total_amount ?? 0) || this.payments().reduce((sum, p) => sum + this.getLendeePayableAmount(p), 0));
          this.listAggregateIds.set(
            body?.contract_ids ??
            body?.papa_ids ??
            body?.depa_ids ??
            body?.missing_payment_ids ??
            body?.dispute_ids ??
            null
          );
          this.clearRowSelection();
          this.payableCount.set(this.totalCount());
          this.partnerTileSummary.updateFromListResponse(this.productKey, body);
          this.refreshFullListTotals(config.productServerName);
          const sourceBalance = this.normalizeAvailableSourceBalance(body?.available_source_balance);
          this.availableSourceBalance.set(sourceBalance);
          this.availableSources.set(this.buildFundingSourceOptions(sourceBalance));
          this.syncUrgentNotesForFilter();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (this.accessService.isAccessDenied(err)) {
            this.accessService.handleAccessDenied(this.productKey, this.router, this.route);
          }
          this.payments.set([]);
          this.totalCount.set(0);
          this.totalPages.set(0);
          this.availableBalance.set(0);
          this.pendingBalance.set(0);
          this.totalAmount.set(0);
          this.listAggregateIds.set(null);
          this.payableCount.set(0);
          this.overviewCalculating.set(false);
          this.availableSourceBalance.set({ card: 0, bank_account: 0 });
          this.availableSources.set([]);
          this.clearRowSelection();
          this.partnerTileSummary.updateFromListResponse(this.productKey, {});
          this.syncOverviewFromListTotals();
          this.syncUrgentNotesForFilter();
        },
      });
  }

  private refreshFullListTotals(productServerName: string): void {
    const total = this.totalCount();
    if (total <= this.pageSize) {
      this.syncOverviewFromListTotals();
      return;
    }

    this.overviewCalculating.set(true);
    this.adapterService
      .listPayments(this.productKey, productServerName, this.buildListFilters({ limit: total, offset: 0 }))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const body = this.isLendeeFilter() && res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res;
          this.totalAmount.set(Number(body?.total_amount ?? this.totalAmount()));
          this.listAggregateIds.set(
            body?.contract_ids ??
            body?.papa_ids ??
            body?.depa_ids ??
            body?.missing_payment_ids ??
            body?.dispute_ids ??
            this.listAggregateIds()
          );
          this.partnerTileSummary.updateFromListResponse(this.productKey, body);
          this.syncOverviewFromListTotals();
          this.overviewCalculating.set(false);
        },
        error: () => {
          this.syncOverviewFromListTotals();
          this.overviewCalculating.set(false);
        },
      });
  }

  paymentId(payment: UnifiedUnpaidPayment): string | number | null {
    return this.adapterService.getPaymentIdentifier(this.productKey, payment, {
      fm_filter: this.fmFilter(),
      fm_status_filter: this.fmStatusFilter(),
      fm_partner_status: this.fmPartnerStatus(),
    });
  }

  private usesIndexRowSelection(): boolean {
    return this.fmFilter() === 14;
  }

  private getRowSelectionKey(row: UnifiedUnpaidPayment): string {
    return String(row[UnpaidPaymentsFmComponent.ROW_SELECTION_KEY] ?? '');
  }

  private annotatePaymentsWithSelectionKeys(rows: UnifiedUnpaidPayment[]): UnifiedUnpaidPayment[] {
    if (!this.usesIndexRowSelection()) return rows;
    return rows.map((row, index) => ({
      ...row,
      [UnpaidPaymentsFmComponent.ROW_SELECTION_KEY]: `${this.page()}-${index}`,
    }));
  }

  rowId(payment: UnifiedUnpaidPayment): string {
    if (this.usesIndexRowSelection()) {
      return this.getRowSelectionKey(payment);
    }
    const id = this.paymentId(payment);
    return id === null ? '' : String(id);
  }

  private clearRowSelection(): void {
    this.selectedIds.set([]);
    this.selectedPaymentsByKey.set(new Map());
  }

  formatTableDate(value: unknown): string {
    return this.calculationService.formatUnpaidTableDate(value);
  }

  get tableHeaders(): TableHeader[] {
    const nowrap: Pick<TableHeader, 'colClass'> = { colClass: 'whitespace-nowrap' };
    const headers: TableHeader[] = [];

    if (this.showContractIdColumn()) {
      headers.push({ key: 'contractId', label: 'Contract ID/Payment ID', sortable: false, ...nowrap });
    }
    headers.push(
      { key: 'businessOwner', label: 'Business Owner Name', sortable: false, ...nowrap },
      { key: 'businessOwnerEmail', label: 'Business Owner Email', sortable: false, ...nowrap }
    );

    if (this.showBusinessNameColumn()) {
      headers.push({ key: 'businessName', label: 'Business Name', sortable: false, ...nowrap });
    }
    if (this.showOfferTypeColumn()) {
      headers.push({ key: 'offerType', label: 'Offer Type', sortable: false, ...nowrap });
    }
    if (this.showRewardPrizeColumn()) {
      headers.push({ key: 'rewardPrize', label: 'Reward Prize', sortable: false, ...nowrap });
    }
    if (this.showStatusColumn()) {
      headers.push({ key: 'status', label: 'Status', sortable: false, ...nowrap });
    }
    if (this.showOrpFilters()) {
      headers.push(
        { key: 'orp', label: 'ORP', sortable: false, ...nowrap },
        { key: 'instantClaim', label: 'Instant Claim', sortable: false, ...nowrap }
      );
    }
    if (this.showLastContractDateColumn()) {
      headers.push({ key: 'lastContractDate', label: 'Last Contract Date', sortable: false, ...nowrap });
    }
    if (this.showPaymentDateColumn()) {
      headers.push({ key: 'paymentDate', label: 'Payment Date', sortable: false, ...nowrap });
    }
    if (this.showPartnerColumns()) {
      headers.push(
        { key: 'partnerName', label: 'Partner Name', sortable: false, ...nowrap },
        { key: 'partnerEmail', label: 'Partner Email', sortable: false, ...nowrap }
      );
    }
    if (this.showRecurringAmountColumn()) {
      headers.push({
        key: 'recurringAmount',
        label: 'Recurring Amount',
        sortable: false,
        ...nowrap,
      });
    }
    if (this.showTransferToBusinessColumn()) {
      headers.push({
        key: 'transferToBusiness',
        label: 'Transfer To Business',
        sortable: false,
        ...nowrap,
      });
    }
    if (this.showPayableAmountColumn()) {
      headers.push({
        key: 'payableAmount',
        label: 'Payable Amount',
        sortable: false,
        ...nowrap,
      });
    }
    if (this.showShareFromBusinessColumn()) {
      headers.push({ key: 'shareFromBusiness', label: 'Share from Business', sortable: false, ...nowrap });
    }
    if (this.showBankAddedColumn()) {
      headers.push({ key: 'bankAdded', label: 'Bank Added', sortable: false, ...nowrap });
    }
    headers.push({ key: 'action', label: 'Actions', sortable: false, align: 'text-left', ...nowrap });
    return headers;
  }

  getAccountCode(payment: UnifiedUnpaidPayment): string {
    return payment['contract_code'] || payment['payment_process_code'] || String(this.paymentId(payment) ?? '-');
  }

  getBusinessOwnerName(payment: UnifiedUnpaidPayment): string {
    return payment['doctor_name'] || '-';
  }

  getBusinessOwnerEmail(payment: UnifiedUnpaidPayment): string {
    return payment['doctor_email'] || payment['email'] || '-';
  }

  getBusinessName(payment: UnifiedUnpaidPayment): string {
    return payment['practice_name'] || '-';
  }

  getStatusLabel(payment: UnifiedUnpaidPayment): string {
    if (this.fmFilter() === 1) {
      return payment['is_partner_active'] || '-';
    }
    const doctorStatus = payment['doctor_status'] || '-';
    if (doctorStatus === 'Inactive' && payment['last_active_days'] > 0) {
      return `${doctorStatus} (Since ${payment['last_active_days']} days)`;
    }
    return doctorStatus;
  }

  getStatusTone(label: string): 'success' | 'danger' | 'warning' | 'neutral' {
    const normalized = (label || '').toLowerCase();
    if (normalized.includes('inactive')) return 'danger';
    if (normalized.includes('active')) return 'success';
    if (normalized.includes('urgent')) return 'warning';
    return 'neutral';
  }

  isUrgent(payment: UnifiedUnpaidPayment): boolean {
    return Number(payment['is_urgent']) === 1;
  }

  hasUrgentNotes(payment: UnifiedUnpaidPayment): boolean {
    return this.getUrgentNotesForPayment(payment).length > 0;
  }

  getUrgentNotesForPayment(payment: UnifiedUnpaidPayment): any[] {
    const meta = this.getUrgentKeyMeta(payment);
    if (meta.value === undefined || meta.value === null || meta.value === '') return [];
    const paymentId = String(meta.value);
    return this.urgentNotes().filter(
      (note) => String(note['payment_id']) === paymentId && note['payment_key'] === meta.key
    );
  }

  showUrgentNotes(payment: UnifiedUnpaidPayment): void {
    this.unpaidPaymentsModal.openNotes({
      title: 'Notes',
      notes: this.getUrgentNotesForPayment(payment),
      emptyMessage: 'No urgent notes found for this payment.',
    });
  }

  isVip(payment: UnifiedUnpaidPayment): boolean {
    return Number(payment['is_vip']) === 1;
  }

  isRowActionsDisabled(): boolean {
    return this.paying() || this.adminLoading() || this.markingUrgent() || this.selectionCount() > 0;
  }

  canRemoveFromUnpaid(): boolean {
    return [3, 4, 7].includes(this.fmFilter());
  }

  supportsUrgentRowActions(): boolean {
    return (this.urgentActionFilters as readonly number[]).includes(this.fmFilter());
  }

  getRowMenuOptions(row: UnifiedUnpaidPayment): DropdownOption[] {
    const cacheKey = `${this.rowId(row)}:${this.fmFilter()}:${this.isUrgent(row)}:${this.hasUrgentNotes(row)}`;
    if (this.rowMenuOptionsCache.has(cacheKey)) {
      return this.rowMenuOptionsCache.get(cacheKey)!;
    }

    const options: DropdownOption[] = [];

    if (this.supportsUrgentRowActions()) {
      if (!this.isUrgent(row)) {
        options.push({
          value: 'mark_urgent',
          label: 'Mark as Urgent Payment',
          icon: 'zap',
          iconClass: 'text-[#ad46ff]',
        });
      } else {
        options.push({
          value: 'remove_urgent',
          label: 'Remove Urgent',
          icon: 'zap-off',
          iconClass: 'text-[#ad46ff]',
        });
      }

      if (this.hasUrgentNotes(row)) {
        options.push({
          value: 'notes',
          label: 'Notes',
          icon: 'clipboard-list',
          iconClass: 'text-[#1581e5]',
        });
      }
    }

    if (this.canRemoveFromUnpaid()) {
      options.push({
        value: 'remove_unpaid',
        label: 'Remove From Unpaid',
        icon: 'trash2',
        iconClass: 'text-[#f59e0b]',
      });
    }

    this.rowMenuOptionsCache.set(cacheKey, options);
    return options;
  }

  getDisplayRowMenuOptions(row: UnifiedUnpaidPayment): DropdownOption[] {
    if (this.isLendeeFilter()) return [];
    return this.getRowMenuOptions(row);
  }

  onRowMenuOptionSelect(value: string | number, row: UnifiedUnpaidPayment, event: Event): void {
    event.stopPropagation();
    const raw = String(value);
    if (raw === 'view_logs') {
      this.showLendeeViewLogs(row);
    } else if (raw === 'remove_unpaid') {
      this.removeRowFromUnpaid(row);
    } else if (raw === 'mark_urgent') {
      this.markPaymentAsUrgent(row, this.openActionMenuRowIndex());
    } else if (raw === 'remove_urgent') {
      this.removePaymentFromUrgent(row, this.openActionMenuRowIndex());
    } else if (raw === 'notes') {
      this.showUrgentNotes(row);
    }
    this.closeActionMenu();
  }

  isActionMenuOpen(row: UnifiedUnpaidPayment): boolean {
    const id = this.rowId(row);
    return !!id && this.openActionMenuRowId() === id;
  }

  toggleActionMenu(row: UnifiedUnpaidPayment, event: Event, rowIndex?: number): void {
    event.stopPropagation();
    if (this.isRowActionsDisabled()) return;
    const id = this.rowId(row);
    if (!id) return;
    if (this.openActionMenuRowId() === id) {
      this.closeActionMenu();
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    this.actionMenuTrigger = trigger;
    this.openActionMenuRowId.set(id);
    this.openActionMenuRow.set(row);
    this.openActionMenuRowIndex.set(rowIndex ?? null);
    this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row));
    setTimeout(() => this.refineActionMenuPosition(), 0);
  }

  closeActionMenu(): void {
    this.openActionMenuRowId.set(null);
    this.openActionMenuRow.set(null);
    this.openActionMenuRowIndex.set(null);
    this.actionMenuPosition.set(null);
    this.actionMenuTrigger = null;
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onActionMenuViewportChange(): void {
    const trigger = this.actionMenuTrigger;
    const row = this.openActionMenuRow();
    if (trigger && row && this.openActionMenuRowId()) {
      const measuredHeight =
        this.actionMenuPanel?.nativeElement.offsetHeight ?? this.estimateActionMenuHeight(row);
      this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row, measuredHeight));
    }
  }

  removeRowFromUnpaid(row: UnifiedUnpaidPayment): void {
    if (!this.canRemoveFromUnpaid()) return;

    this.unpaidPaymentsModal
      .openRemoveUnpaid({
        contractId: this.getAccountCode(row),
        title: 'Are you sure you want to remove this payment from Unpaid ?',
        confirmLabel: 'Remove',
        inputPlaceholder: 'Enter note',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.submitRemoveFromUnpaid(row, result.note);
      });
  }

  markPaymentAsUrgent(payment: UnifiedUnpaidPayment, rowIndex: number | null): void {
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.updateUrgentState(payment, result.note, true, rowIndex);
      });
  }

  removePaymentFromUrgent(payment: UnifiedUnpaidPayment, rowIndex: number | null): void {
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.updateUrgentState(payment, result.note, false, rowIndex);
      });
  }

  private updateUrgentState(
    payment: UnifiedUnpaidPayment,
    note: string,
    makeUrgent: boolean,
    rowIndex: number | null
  ): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    const meta = this.getUrgentKeyMeta(payment);
    if (!meta.value) {
      this.openActionError('This payment does not expose the required identifier.', 'Urgent Action Unavailable');
      return;
    }

    const actionLabel = makeUrgent ? 'mark payment as urgent' : 'remove payment from urgent';
    this.markingUrgent.set(true);
    this.adapterService
      .request(
        this.productKey,
        config.productServerName,
        'post',
        this.adapterService.resolveProductApiPath(
          this.productKey,
          `finance/${makeUrgent ? 'mark_payment_urgent' : 'mark_payment_non_urgent'}`
        ),
        {
          primary_key_name: meta.key,
          payment_id: meta.value,
          filter: String(this.fmFilter()),
          index: rowIndex != null ? String(rowIndex) : undefined,
          primary_key_name2: meta.key,
          is_urgent: makeUrgent ? '1' : '0',
          admin_notes: note,
          country_id: this.adapterService.getProductCountryId(this.productKey),
        },
        this.buildListFilters()
      )
      .subscribe({
        next: (res: any) => {
          this.markingUrgent.set(false);
          if (res?.is_error) {
            this.openActionError(
              this.formatApiErrorMessage(res) || `Unable to ${actionLabel}.`,
              'Urgent Action Failed'
            );
            return;
          }
          const nextRows = [...this.payments()];
          const idx = nextRows.findIndex((row) => this.paymentId(row) === this.paymentId(payment));
          if (idx > -1) {
            nextRows[idx] = { ...nextRows[idx], is_urgent: makeUrgent ? 1 : 0 };
          }
          this.rowMenuOptionsCache.clear();
          this.payments.set(nextRows);
          this.fetchUrgentNotes();
        },
        error: () => {
          this.markingUrgent.set(false);
          this.openActionError(`Unable to ${actionLabel}.`, 'Urgent Action Failed');
        },
      });
  }

  private syncUrgentNotesForFilter(): void {
    if (this.supportsUrgentRowActions()) {
      this.fetchUrgentNotes();
      return;
    }
    this.urgentNotes.set([]);
    this.rowMenuOptionsCache.clear();
  }

  private fetchUrgentNotes(): void {
    if (!this.supportsUrgentRowActions()) return;

    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.adapterService
      .request<any>(
        this.productKey,
        config.productServerName,
        'get',
        this.adapterService.resolveProductApiPath(this.productKey, 'finance/fetch_unpaid_urgent_notes'),
        {
          country_id: this.adapterService.getProductCountryId(this.productKey),
          show_toaster_messages: false,
        }
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.urgentNotes.set(res?.data?.notes_list || []);
          this.rowMenuOptionsCache.clear();
        },
        error: () => {
          this.urgentNotes.set([]);
          this.rowMenuOptionsCache.clear();
        },
      });
  }

  private getUrgentKeyMeta(payment: UnifiedUnpaidPayment): { key: string; value: any } {
    switch (this.fmFilter()) {
      case 9:
        // Legacy FM: filter 9 uses papa_id only when active_status is '2' (not Partner tab '1').
        return this.fmStatusFilter() === '2'
          ? { key: 'papa_id', value: payment['papa_id'] }
          : { key: 'dopa_id', value: payment['dopa_id'] };
      case 14:
        return { key: 'cashback_id', value: payment['cashback_id'] };
      default:
        return { key: 'dopa_id', value: payment['dopa_id'] };
    }
  }

  getPartnerName(payment: UnifiedUnpaidPayment): string {
    return `${payment['partner_title'] ? payment['partner_title'] + ' ' : ''}${payment['partner_name'] || '-'}`;
  }

  getPartnerEmail(payment: UnifiedUnpaidPayment): string {
    return payment['partner_email'] || '-';
  }

  getAmountCharged(payment: UnifiedUnpaidPayment): number {
    return Number(payment['recurring_amount'] || payment['amount_deducted'] || 0);
  }

  getPayableAmount(payment: UnifiedUnpaidPayment): number {
    if (this.fmFilter() === 1 || this.fmFilter() === 6 || (this.fmFilter() === 9 && this.fmStatusFilter() === '1')) {
      return Number(payment['pay_to_partner'] || 0);
    }
    if (this.fmFilter() === 2) {
      return Number(payment['amount'] || 0);
    }
    if (this.fmFilter() === 4) {
      if (this.fmStatusFilter() === '3') {
        return Number(payment['pay_to_partner'] || 0);
      }
      return Number(payment['amount_deducted'] || 0);
    }
    return Number(payment['pay_to_doctor'] || 0);
  }

  getBankAdded(payment: UnifiedUnpaidPayment): string {
    if (this.fmFilter() === 14) {
      return payment['bank_added'] === 'Yes' || String(payment['bank_added'] || '').startsWith('acct_')
        ? 'Yes'
        : 'No';
    }
    if ([5, 6, 8, 11, 12, 13].includes(this.fmFilter())) {
      return payment['bank_added'] === 'Yes' || payment['stripe_account_id'] != null ? 'Yes' : 'No';
    }
    return payment['bank_added'] || '-';
  }

  showBankAddedTransferDisabled(row: UnifiedUnpaidPayment): boolean {
    const filter = this.fmFilter();
    if (filter === 12) {
      return row['transfers_disabled'] === true;
    }
    if ([5, 8, 11, 13, 14].includes(filter)) {
      return row['transfers_disabled'] != 1 && row['transfers_disabled'] !== '1';
    }
    return false;
  }

  getOfferTypeLabel(payment: UnifiedUnpaidPayment): string {
    return payment['offer_type'] === 1 ? '$15 Challenge' : payment['offer_type'] === 2 ? '$100 Challenge' : '-';
  }

  getRewardPrizeLabel(payment: UnifiedUnpaidPayment): string {
    return payment['reward_cycle'] === 1
      ? '1st Reward Prize'
      : payment['reward_cycle'] === 2
        ? '2nd Reward Prize'
        : payment['reward_cycle'] === 3
          ? '3rd Reward Prize'
          : '-';
  }

  getShareFromBusinessLabel(payment: UnifiedUnpaidPayment): string {
    return payment['share_from_doctor'] ? 'Yes' : 'No';
  }

  getTransferToBusinessAmount(payment: UnifiedUnpaidPayment): number {
    return Number(payment['pay_to_doctor'] || payment['amount_deducted'] || 0);
  }

  getRowAmountDisplay(payment: UnifiedUnpaidPayment): number {
    if (this.showTransferToBusinessColumn()) {
      return this.getTransferToBusinessAmount(payment);
    }
    return this.getPayableAmount(payment);
  }

  getAmountSymbol(payment: UnifiedUnpaidPayment): string {
    return payment['display_logo'] || '$';
  }

  showContractIdColumn(): boolean {
    return this.fmFilter() !== 13;
  }

  showBusinessNameColumn(): boolean {
    return this.fmFilter() === 13;
  }

  showOfferTypeColumn(): boolean {
    return this.fmFilter() === 13;
  }

  showRewardPrizeColumn(): boolean {
    return this.fmFilter() === 13;
  }

  showPartnerColumns(): boolean {
    return (
      this.fmFilter() === 1 ||
      this.fmFilter() === 6 ||
      (this.fmFilter() === 4 && this.fmStatusFilter() === '3') ||
      (this.fmFilter() === 9 && this.fmStatusFilter() === '1')
    );
  }

  showStatusColumn(): boolean {
    return (
      this.fmFilter() !== 2 &&
      this.fmFilter() !== 9 &&
      this.fmFilter() !== 10 &&
      this.fmFilter() !== 13 &&
      this.fmStatusFilter() !== '3'
    );
  }

  showLastContractDateColumn(): boolean {
    return (
      this.fmFilter() !== 2 &&
      this.fmFilter() !== 1 &&
      this.fmFilter() !== 6 &&
      this.fmFilter() !== 9 &&
      this.fmFilter() !== 10 &&
      this.fmFilter() !== 12 &&
      this.fmFilter() !== 13 &&
      this.fmStatusFilter() !== '3'
    );
  }

  showPaymentDateColumn(): boolean {
    return this.fmFilter() === 12;
  }

  showRecurringAmountColumn(): boolean {
    return this.fmFilter() !== 9 && this.fmFilter() !== 10 && this.fmFilter() !== 12 && this.fmFilter() !== 13;
  }

  showTransferToBusinessColumn(): boolean {
    return this.fmFilter() === 4 && this.fmStatusFilter() !== '3';
  }

  showPayableAmountColumn(): boolean {
    return !(this.fmFilter() === 4 && this.fmStatusFilter() !== '3');
  }

  showShareFromBusinessColumn(): boolean {
    return this.fmFilter() === 1 || this.fmFilter() === 6;
  }

  showBankAddedColumn(): boolean {
    return [1, 5, 6, 8, 11, 13, 14].includes(this.fmFilter());
  }

  showVipFilter(): boolean {
    return true;
  }

  showStatusFilter(): boolean {
    return this.fmFilter() !== 2 && this.fmFilter() !== 13;
  }

  showPartnerStatusFilter(): boolean {
    return this.fmFilter() === 1 || this.fmFilter() === 6;
  }

  showOrpFilterControl(): boolean {
    return true;
  }

  orpFilterDisabled(): boolean {
    return this.fmFilter() !== 3;
  }

  showInstantClaimFilter(): boolean {
    return this.fmFilter() !== 1 && this.fmFilter() !== 6;
  }

  instantClaimFilterDisabled(): boolean {
    return this.fmFilter() !== 3;
  }

  showOrpFilters(): boolean {
    return this.fmFilter() === 3;
  }

  canSelectRow(payment: UnifiedUnpaidPayment): boolean {
    return this.paymentId(payment) !== null;
  }

  isRowPayable(payment: UnifiedUnpaidPayment): boolean {
    if (this.paymentId(payment) === null) return false;
    if (this.calculationService.isRowTransferDisabled(payment)) return false;
    return payment['bank_added'] !== 'No';
  }

  canPayRow(payment: UnifiedUnpaidPayment): boolean {
    if (!this.isRowPayable(payment)) return false;
    if (this.selectionCount() > 0) return false;
    return true;
  }

  onSelectionChange(selectedRows: UnifiedUnpaidPayment[]): void {
    if (this.usesIndexRowSelection()) {
      const currentPageKeys = new Set(this.payments().map((p) => this.getRowSelectionKey(p)));
      const next = new Map(this.selectedPaymentsByKey());
      for (const key of currentPageKeys) {
        next.delete(key);
      }
      for (const row of selectedRows) {
        const key = this.getRowSelectionKey(row);
        if (key) next.set(key, row);
      }
      this.selectedPaymentsByKey.set(next);
      return;
    }

    const currentPageIds = this.payments()
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    const selectedCurrentPageIds = selectedRows
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    const otherPageIds = this.selectedIds().filter((id) => !currentPageIds.includes(id));
    this.selectedIds.set([...otherPageIds, ...selectedCurrentPageIds]);
  }

  toggleSelectAllVisible(): void {
    if (this.usesIndexRowSelection()) {
      const currentPageKeys = new Set(this.payments().map((p) => this.getRowSelectionKey(p)));
      const next = new Map(this.selectedPaymentsByKey());
      if (this.allVisibleSelected()) {
        for (const key of currentPageKeys) {
          next.delete(key);
        }
      } else {
        for (const row of this.payments().filter((p) => this.canSelectRow(p))) {
          const key = this.getRowSelectionKey(row);
          if (key) next.set(key, row);
        }
      }
      this.selectedPaymentsByKey.set(next);
      return;
    }

    if (this.allVisibleSelected()) {
      this.clearRowSelection();
      return;
    }
    const ids = this.payments()
      .filter((p) => this.canSelectRow(p))
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    this.selectedIds.set(ids);
  }

  paySingle(payment: UnifiedUnpaidPayment): void {
    if (this.isLendeeFilter()) {
      this.confirmPayLendeeRow(payment);
      return;
    }

    if (!this.canPayRow(payment)) return;
    this.confirmPayRows([payment], 'Confirm payment');
  }

  paySelected(): void {
    const picked = this.selectedPayablePayments();
    if (!picked.length) return;
    this.confirmPayRows(picked, 'Confirm paying selected');
  }

  payAllVisible(): void {
    const rows = this.payments().filter((p) => this.isRowPayable(p));
    if (!rows.length) return;
    this.confirmPayRows(rows, 'Confirm paying all visible');
  }

  payAllToBusiness(): void {
    const count = this.payableCount();
    if (!count) return;
    const total = this.totalAmount() || this.overview().totalPaymentToPay;
    this.confirmPayAll(total, count);
  }

  payAllUrgentVisible(): void {
    const rows = this.visibleUrgentRows();
    if (!rows.length) return;
    this.confirmPayRows(rows, 'Confirm paying all urgent visible');
  }

  onFilterChange(next: number): void {
    if (!this.supportedFilters.includes(next as (typeof this.supportedFilters)[number])) return;
    this.navigateWithFilters({
      filter: next,
      page: 1,
      partnerStatus: '0',
      orp: -1,
      instant: -1,
      challenge: next !== 13 ? '0' : this.challengeType(),
      status: '0',
      ...(next !== 15 ? { lendeeShareType: 0, lendeePaymentStatus: 0 } : {}),
    });
  }

  onFilterDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.onFilterChange(Number(raw));
  }

  onStatusDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ status: String(raw), page: 1 });
  }

  onVipDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ vip: Number(raw), page: 1 });
  }

  onPartnerStatusDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ partnerStatus: String(raw), page: 1 });
  }

  onOrpDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ orp: Number(raw), page: 1 });
  }

  onInstantClaimDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ instant: Number(raw), page: 1 });
  }

  onChallengeDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ challenge: String(raw), page: 1 });
  }

  onExportDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === 'xlsx') {
      this.exportRows(false);
    } else if (raw === 'csv') {
      this.exportRows(true);
    }
  }

  applyFilters(): void {
    if (this.isLendeeFilter()) {
      this.applyLendeeFilters();
      return;
    }
    this.onSearch();
  }

  setUrgentOnly(checked: boolean): void {
    if (this.urgentOnly() === checked) return;
    this.navigateWithFilters({ urgent: checked, page: 1 });
  }

  onSearch(): void {
    this.navigateWithFilters({ search: this.search(), page: 1 });
  }

  onLendeeSearch(): void {
    if (!this.isLendeeFilter()) return;
    this.navigateWithFilters({ search: this.searchDraft(), page: 1 });
  }

  clearSearch(): void {
    this.navigateWithFilters({ search: '', page: 1 });
  }

  resetFilters(): void {
    if (this.isLendeeFilter()) {
      this.resetLendeeFilters();
      return;
    }
    this.navigateWithFilters({
      search: '',
      status: '0',
      partnerStatus: '0',
      orp: -1,
      instant: -1,
      vip: 0,
      urgent: false,
      challenge: '0',
      page: 1,
    });
  }

  goToPage(next: number): void {
    if (next < 1 || next > this.totalPages()) return;
    this.navigateWithFilters({ page: next });
  }

  exportRows(asCsv = false): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.exporting.set(true);
    const filters = this.buildListFilters({
      limit: this.totalCount() || this.pageSize,
      offset: 0,
    });
    this.adapterService
      .listPayments(this.productKey, config.productServerName, filters)
      .subscribe({
        next: (res: any) => {
          this.exporting.set(false);
          const rows = Array.isArray(res?.payments) ? res.payments : [];
          const exportRows = rows.map((payment: UnifiedUnpaidPayment) => ({
            Account: this.getAccountCode(payment),
            'Business Owner Name': this.getBusinessOwnerName(payment),
            'Business Owner Email': this.getBusinessOwnerEmail(payment),
            'Business Name': this.getBusinessName(payment),
            Status: this.getStatusLabel(payment),
            'Partner Name': this.showPartnerColumns() ? this.getPartnerName(payment) : '',
            'Partner Email': this.showPartnerColumns() ? this.getPartnerEmail(payment) : '',
            'Recurring Amount': this.showRecurringAmountColumn() ? this.getAmountCharged(payment) : '',
            'Payable Amount': this.getPayableAmount(payment),
            'Bank Added': this.showBankAddedColumn() ? this.getBankAdded(payment) : '',
            VIP: Number(payment['is_vip']) === 1 ? 'Yes' : 'No',
            Urgent: Number(payment['is_urgent']) === 1 ? 'Yes' : 'No',
            'Payment Date': payment['date_added'] || '',
          }));
          const sheet = XLSX.utils.json_to_sheet(exportRows);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, sheet, 'FM Unpaid');
          XLSX.writeFile(workbook, asCsv ? 'fm_unpaid_payments.csv' : 'fm_unpaid_payments.xlsx');
        },
        error: () => {
          this.exporting.set(false);
          this.openActionError('FM export could not be generated. Please try again.', 'Export Failed');
        },
      });
  }

  private confirmPayAll(total: number, paymentCount: number): void {
    const openPaymentConfirm = (transferSource?: UnpaidPaymentsFundingSourceId): void => {
      const insufficient = this.fmFilter() !== 12 && total > this.availableBalance();
      this.unpaidPaymentsModal
        .openConfirmPayment({
          title: this.paymentConfirmTitle,
          totalAmount: total,
          paymentCount,
          confirmLabel: 'Pay',
          cancelLabel: 'Cancel',
          confirmDisabled: insufficient,
          disabledMessage: insufficient
            ? `Insufficient balance. Amount due is $${total.toFixed(2)} but available balance is $${this.availableBalance().toFixed(2)}.`
            : undefined,
        })
        .subscribe((result) => {
          if (!result?.confirmed || insufficient) return;
          this.payAll(total, transferSource);
        });
    };

    if (this.shouldUseTransferFromModal()) {
      if (!this.buildTransferSourceOptions().length) {
        this.openPaymentError('No funding source is available for this payout.');
        return;
      }
      this.openTransferModal(total, (source) => openPaymentConfirm(source));
      return;
    }

    openPaymentConfirm();
  }

  private confirmPayRows(rows: UnifiedUnpaidPayment[], _title: string): void {
    const total = rows.reduce((sum, row) => sum + this.getPayableAmount(row), 0);

    if (this.shouldUseTransferFromModal()) {
      if (!this.buildTransferSourceOptions().length) {
        this.openPaymentError('No funding source is available for this payout.');
        return;
      }
      this.openTransferModal(total, (source) => this.payByRows(rows, total, source));
      return;
    }

    const insufficient = this.fmFilter() !== 12 && total > this.availableBalance();
    this.unpaidPaymentsModal
      .openConfirmPayment({
        title: this.paymentConfirmTitle,
        totalAmount: total,
        paymentCount: rows.length,
        confirmLabel: 'Pay',
        cancelLabel: 'Cancel',
        confirmDisabled: insufficient,
        disabledMessage: insufficient
          ? `Insufficient balance. Selected total is $${total.toFixed(2)} but available balance is $${this.availableBalance().toFixed(2)}.`
          : undefined,
      })
      .subscribe((result) => {
        if (!result?.confirmed || insufficient) return;
        this.payByRows(rows, total);
      });
  }

  private confirmPayLendeeRow(row: UnifiedUnpaidPayment): void {
    if (this.isLendeePaid(row)) return;
    const payId = this.getLendeePayId(row);
    if (!payId) {
      this.openPaymentError('This lendee payment does not expose the required pay ID.');
      return;
    }

    const total = this.getLendeePayableAmount(row);
    this.unpaidPaymentsModal
      .openConfirmPayment({
        title: this.paymentConfirmTitle,
        totalAmount: total,
        paymentCount: 1,
        confirmLabel: 'Pay',
        cancelLabel: 'Cancel',
      })
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.payLendeeUnpaid(row, total);
      });
  }

  private openTransferModal(total: number, onConfirm: (source: UnpaidPaymentsFundingSourceId) => void): void {
    const sources = this.buildTransferSourceOptions();
    if (!sources.length) {
      this.openPaymentError('No funding source is available for this payout.');
      return;
    }

    this.unpaidPaymentsModal
      .openTransferFrom({
        totalAmount: total,
        sources,
        title: 'Transfer From',
        confirmLabel: 'Pay',
      })
      .subscribe((result) => {
        if (!result?.confirmed) return;

        const available = sources.find((source) => source.id === result.source)?.availableAmount ?? 0;
        if (available < total) {
          this.openPaymentError(
            `Insufficient ${result.source === 'card' ? 'card' : 'bank'} balance. Available: $${available.toFixed(2)}.`
          );
          return;
        }

        onConfirm(result.source);
      });
  }

  private payByRows(
    rows: UnifiedUnpaidPayment[],
    paidAmount: number,
    transferSource?: UnpaidPaymentsFundingSourceId
  ): void {
    const product = this.getProduct();
    if (!product || !rows.length) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.paying.set(true);
    this.adapterService
      .releasePayments(this.productKey, config.productServerName, rows, {
        ...this.buildListFilters(),
        transfer_from: this.normalizeTransferSource(transferSource),
      })
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          const errors = Array.isArray(res?.error_tracking) ? res.error_tracking : [];
          if (errors.length) {
            this.showErrorTracking(errors, 'Payment completed with issues');
            this.clearRowSelection();
            this.fetchPayments();
            return;
          }
          if (res?.is_error === 0 || res?.is_error == null) {
            this.clearRowSelection();
            this.unpaidPaymentsModal
              .openPaymentStatus({
                amount: paidAmount,
                statusMessage: 'Paid Successfully',
                confirmLabel: 'Done',
              })
              .subscribe(() => {
                this.fetchPayments();
              });
            return;
          }
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage: res?.original_text || res?.message || 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
        },
        error: () => {
          this.paying.set(false);
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage: 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
        },
      });
  }

  private payLendeeUnpaid(row: UnifiedUnpaidPayment, total: number): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;
    const payId = this.getLendeePayId(row);
    if (!payId) {
      this.openPaymentError('This lendee payment does not expose the required pay ID.');
      return;
    }

    this.paying.set(true);
    this.adapterService
      .request<any>(
        this.productKey,
        config.productServerName,
        'post',
        'admin/unpaid_payments/pay_lendee_unpaid',
        {
          pay_id: payId,
          country_id: 1,
          show_toaster_messages: false,
          device_info: this.buildDeviceInfo(),
        },
        this.buildListFilters()
      )
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          if (res?.is_error) {
            this.openPaymentError(res?.message || res?.original_text || 'Unable to complete the payment.');
            return;
          }
          this.unpaidPaymentsModal
            .openPaymentStatus({
              amount: total,
              statusMessage: res?.message || 'Paid Successfully',
              confirmLabel: 'Done',
            })
            .subscribe(() => this.fetchPayments());
        },
        error: () => {
          this.paying.set(false);
          this.openPaymentError('Unable to complete the payment.');
        },
      });
  }

  private payAll(paidAmount: number, transferSource?: UnpaidPaymentsFundingSourceId): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.paying.set(true);
    this.adapterService
      .releaseAllPayments(this.productKey, config.productServerName, {
        ...this.buildListFilters(),
        aggregate_ids: this.listAggregateIds(),
        transfer_from: this.normalizeTransferSource(transferSource),
      })
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          const errors = Array.isArray(res?.error_tracking) ? res.error_tracking : [];
          if (errors.length) {
            this.showErrorTracking(errors, 'Payment completed with issues');
            this.clearRowSelection();
            this.fetchPayments();
            return;
          }
          if (res?.is_error === 0 || res?.is_error == null) {
            this.clearRowSelection();
            this.unpaidPaymentsModal
              .openPaymentStatus({
                amount: paidAmount,
                statusMessage: 'Paid Successfully',
                confirmLabel: 'Done',
              })
              .subscribe(() => {
                this.fetchPayments();
              });
            return;
          }
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage: res?.original_text || res?.message || 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
        },
        error: () => {
          this.paying.set(false);
          this.unpaidPaymentsModal.openPaymentError({
            title: 'Payment Failed',
            statusMessage: 'Unable to complete the payment.',
            confirmLabel: 'Done',
          });
        },
      });
  }

  private showErrorTracking(errors: any[], title: string): void {
    const subtitle = errors
      .map((error, index) => `${index + 1}. ${error?.error_message || error?.message || JSON.stringify(error)}`)
      .join('\n');
    this.unpaidPaymentsModal.openPaymentError({
      title: 'Payment Status',
      statusMessage: title,
      subtitle: subtitle || 'One or more records failed to process.',
      confirmLabel: 'Done',
    });
  }

  private openActionSuccess(statusMessage: string, title = 'Success', subtitle?: string): void {
    this.unpaidPaymentsModal.openActionSuccess({ title, statusMessage, subtitle }).subscribe();
  }

  private formatApiErrorMessage(res: any): string | undefined {
    const message = res?.message;
    if (typeof message === 'string' && message.trim()) return message;

    const original = res?.original_text;
    if (typeof original === 'string' && original.trim()) return original.replace(/^"|"$/g, '');
    if (original && typeof original === 'object') {
      if (typeof original.sqlMessage === 'string') return original.sqlMessage;
      if (typeof original.message === 'string') return original.message;
    }
    return undefined;
  }

  private openActionError(statusMessage: string, title = 'Action Failed', subtitle?: string): void {
    this.unpaidPaymentsModal.openActionError({ title, statusMessage, subtitle }).subscribe();
  }

  private submitRemoveFromUnpaid(payment: UnifiedUnpaidPayment, note: string): void {
    const filter = this.fmFilter();

    if (filter === 4) {
      const disputeId = payment['dispute_id'];
      const path = disputeId ? 'finance/toggle_unpaid_dispute' : 'finance/remove_unpaid_partner';
      this.runFinanceRequest(
        path,
        { dispute_id: disputeId || payment['papa_id'] },
        'Payment removed from unpaid.'
      );
      return;
    }

    if (filter === 7) {
      this.runFinanceRequest(
        'finance/remove_unpaid_contract_difference',
        { contract_id: payment['contract_id'], notes: note },
        'Removed successfully.'
      );
      return;
    }

    if (filter === 3) {
      const adminId = this.getAdminId();
      this.runFinanceRequest(
        'finance/remove_from_unpaid',
        {
          missing_payment_id: payment['missing_payment_id'],
          dopa_id: payment['doctor_transfer_dopa_id'],
          contract_id: payment['contract_id'],
          notes: note,
          admin_id: adminId,
          claim_status: 9,
        },
        'Removed successfully.'
      );
    }
  }

  private runFinanceRequest(path: string, payload: Record<string, any>, successMessage: string): void {
    const product = this.getProduct();
    if (!product) return;
    const config = this.getProductConfig(product);
    if (!config) return;

    this.adminLoading.set(true);
    this.adapterService
      .request(this.productKey, config.productServerName, 'post', path, payload, this.buildListFilters())
      .subscribe({
        next: (res: any) => {
          this.adminLoading.set(false);
          if (res?.is_error) {
            this.openActionError(res?.message || res?.original_text || 'Unable to complete the action.');
            return;
          }
          this.openActionSuccess(successMessage);
          this.fetchPayments();
        },
        error: () => {
          this.adminLoading.set(false);
          this.openActionError('Unable to complete the action.');
        },
      });
  }

  private getAdminId(): number {
    try {
      const raw = localStorage.getItem('profileData');
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      return Number(parsed?.[0]?.admin_id || parsed?.admin_id || 0);
    } catch {
      return 0;
    }
  }

  private estimateActionMenuHeight(row: UnifiedUnpaidPayment): number {
    return this.getDisplayRowMenuOptions(row).length * this.actionMenuItemHeight + this.actionMenuPadding;
  }

  private computeActionMenuPosition(
    trigger: HTMLElement,
    row: UnifiedUnpaidPayment,
    menuHeight = this.estimateActionMenuHeight(row)
  ): { top: number; left: number } {
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bottomReserve = this.selectionCount() > 0 ? this.actionMenuBottomBarReserve : 0;
    const maxBottom = viewportHeight - bottomReserve - this.actionMenuViewportMargin;
    const maxTop = this.actionMenuViewportMargin;

    const spaceBelow = maxBottom - rect.bottom;
    const spaceAbove = rect.top - maxTop;

    let top =
      spaceBelow >= menuHeight + this.actionMenuGap || spaceBelow >= spaceAbove
        ? rect.bottom + this.actionMenuGap
        : rect.top - menuHeight - this.actionMenuGap;

    top = Math.max(maxTop, Math.min(top, maxBottom - menuHeight));

    let left = rect.right - this.actionMenuWidth;
    left = Math.max(
      this.actionMenuViewportMargin,
      Math.min(left, viewportWidth - this.actionMenuWidth - this.actionMenuViewportMargin)
    );

    return { top, left };
  }

  private refineActionMenuPosition(): void {
    const trigger = this.actionMenuTrigger;
    const row = this.openActionMenuRow();
    if (!trigger || !row || !this.openActionMenuRowId()) return;

    const measuredHeight = this.actionMenuPanel?.nativeElement.offsetHeight;
    if (!measuredHeight) return;

    this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row, measuredHeight));
  }

  private buildFundingSourceOptions(balanceMap: any): { label: string; value: string }[] {
    if (!balanceMap) return [];
    return Object.entries(balanceMap).map(([key, value]) => ({
      label: `${key.replace(/_/g, ' ')} ($${Number(value).toFixed(2)})`,
      value: key,
    }));
  }

  private buildTransferSourceOptions(): UnpaidPaymentsFundingSourceOption[] {
    const balanceMap = this.availableSourceBalance();
    return Object.entries(balanceMap)
      .map(([key, amount]) => {
        const id = this.toFundingSourceId(key);
        if (!id) return null;
        return {
          id,
          label: id === 'card' ? 'Card' : 'Bank',
          availableAmount: Number(amount) || 0,
        } satisfies UnpaidPaymentsFundingSourceOption;
      })
      .filter((source): source is UnpaidPaymentsFundingSourceOption => source !== null);
  }

  private toFundingSourceId(key: string): UnpaidPaymentsFundingSourceId | null {
    const normalized = key.toLowerCase();
    if (normalized.includes('bank')) return 'bank_account';
    if (normalized.includes('card')) return 'card';
    return null;
  }

  /** ACH doctor/partner payouts require choosing card or bank (`transfer_from`). */
  private shouldUseTransferFromModal(): boolean {
    return [5, 6, 8, 10, 11].includes(this.fmFilter());
  }

  /** Amount To Pay overview uses full filtered listing totals, not the current table page. */
  private syncOverviewFromListTotals(): void {
    this.partnerTileSummary.patchOverview(this.productKey, {
      totalPaymentsCount: this.payableCount(),
      totalPaymentToPay: this.totalAmount(),
    });
    this.hubOverviewService.cacheProductOverviewFromPage(this.productKey);
  }

  private refreshPayableCount(pageRows: UnifiedUnpaidPayment[]): void {
    const product = this.getProduct();
    if (!product) {
      this.overviewCalculating.set(false);
      return;
    }
    const config = this.getProductConfig(product);
    if (!config) {
      this.overviewCalculating.set(false);
      return;
    }

    const requestId = ++this.payableCountRequestId;
    const total = this.totalCount();
    this.overviewCalculating.set(true);

    this.calculationService.resolvePayableCountFromListing(
      (limit, offset) =>
        this.adapterService.listPayments(
          this.productKey,
          config.productServerName,
          this.buildListFilters({ limit, offset })
        ),
      total,
      pageRows,
      (payment) => this.isRowPayable(payment)
    ).subscribe({
      next: (count) => {
        if (requestId !== this.payableCountRequestId) return;
        this.payableCount.set(count);
        this.syncOverviewFromListTotals();
        this.overviewCalculating.set(false);
      },
      error: () => {
        if (requestId !== this.payableCountRequestId) return;
        this.payableCount.set(0);
        this.syncOverviewFromListTotals();
        this.overviewCalculating.set(false);
      },
    });
  }

  private normalizeAvailableSourceBalance(raw: unknown): Record<string, number> {
    if (!raw || typeof raw !== 'object') {
      return { card: 0, bank_account: 0 };
    }
    const map = raw as Record<string, unknown>;
    return {
      card: Number(map['card'] ?? map['CARD'] ?? 0) || 0,
      bank_account: Number(map['bank_account'] ?? map['bank'] ?? map['BANK'] ?? 0) || 0,
    };
  }

  private openPaymentError(statusMessage: string, title = 'Payment Failed'): void {
    this.unpaidPaymentsModal.openPaymentError({
      title,
      statusMessage,
      confirmLabel: 'Done',
    });
  }

  private normalizeTransferSource(
    transferSource?: UnpaidPaymentsFundingSourceId
  ): 'BANK' | 'CARD' | undefined {
    if (!transferSource) return undefined;
    return transferSource === 'bank_account' ? 'BANK' : 'CARD';
  }

  getLendeePayableAmount(row: any): number {
    return Number(row?.payout_amount ?? row?.pay_to_user ?? row?.payable_amount ?? row?.amount ?? 0);
  }

  getLendeeShareType(row: any): string {
    const type = row?.beneficiary_type ?? row?.share_type;
    if (type === 1 || type === 'business' || type === 'Business') return 'Business';
    if (type === 2 || type === 'supporter' || type === 'Supporter') return 'Supporter';
    return String(type || '-');
  }

  getLendeeStatus(row: any): string {
    const status = row?.payout_status ?? row?.status;
    if (status === 2 || String(status).toLowerCase() === 'paid') return 'Paid';
    if (status === 0 || status === 1 || String(status).toLowerCase() === 'unpaid') return 'Unpaid';
    return '-';
  }

  isLendeePaid(row: any): boolean {
    return this.getLendeeStatus(row) === 'Paid';
  }

  getLendeeContractCode(row: any): string {
    const contractDetails = this.parseJsonField(row?.contract_details);
    return contractDetails?.contract_code ?? row?.contract_code ?? row?.micro_loan_code ?? row?.record_code ?? '-';
  }

  getLendeeContractType(row: any): string {
    const contractDetails = this.parseJsonField(row?.contract_details);
    const type = contractDetails?.contract_type ?? row?.contract_type;
    if (type === 1) return 'Deferred';
    if (type === 2) return 'FM Assured';
    if (type === 3) return 'FM Performance';
    return contractDetails?.contract_type ?? row?.contract_type_name ?? String(type || '-');
  }

  getLendeeRequestedAmount(row: any): number {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    return Number(supporterDetails?.requested_amount ?? row?.requested_amount ?? row?.early_payout_amount ?? 0);
  }

  getLendeeDuration(row: any): string {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const months = supporterDetails?.offer_duration_months ?? supporterDetails?.term ?? row?.offer_duration_months ?? row?.duration ?? row?.duration_months ?? row?.term;
    return months != null ? String(months) : '-';
  }

  getLendeePaymentReceived(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    return Number(row?.paid_payments ?? paymentDetails?.paid_payments ?? paymentDetails?.payment_received ?? row?.payment_received ?? 0);
  }

  getLendeePaidToSupporter(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    return Number(row?.lup_payment_count ?? paymentDetails?.lup_payment_count ?? paymentDetails?.total_paid_to_supporter ?? row?.paid_to_supporter ?? 0);
  }

  getLendeeBankAdded(row: any): string {
    if (row?.stripe_account_id || row?.bank_added === 'Yes' || row?.bank_added === true) return 'Yes';
    return 'No';
  }

  readonly expandedLendeeRowId = signal<string | null>(null);

  toggleLendeeDetails(row: any): void {
    const id = this.getLendeeRowId(row);
    this.expandedLendeeRowId.set(this.expandedLendeeRowId() === id ? null : id);
  }

  isLendeeRowExpanded(row: any): boolean {
    return this.expandedLendeeRowId() === this.getLendeeRowId(row);
  }

  private getLendeeRowId(row: any): string {
    return String(row?.lup_id ?? row?.contract_id ?? row?.id ?? '');
  }

  private getLendeePayId(row: any): number | string | null {
    return row?.pay_id ?? row?.lup_id ?? row?.id ?? null;
  }

  private buildDeviceInfo(): Record<string, unknown> {
    const userAgent = navigator.userAgent;
    return {
      browser: {
        isFirefox: /firefox/i.test(userAgent),
        isChrome: /chrome|chromium|crios/i.test(userAgent) && !/edg|opr/i.test(userAgent),
        isSafari: /safari/i.test(userAgent) && !/chrome|chromium|crios|android/i.test(userAgent),
        isOpera: /opera|opr/i.test(userAgent),
        isIE: /trident|msie/i.test(userAgent),
        isEdge: /edg/i.test(userAgent),
      },
      platform: navigator.platform,
      userAgent,
    };
  }

  getLendeeBusinessName(row: any): string {
    const contractDetails = this.parseJsonField(row?.contract_details);
    const doctorDetails = this.parseJsonField(row?.doctor_details);
    return contractDetails?.business_name ?? doctorDetails?.practice_name ?? row?.business_name ?? row?.practice_name ?? '-';
  }

  getLendeeFinancedAmount(row: any): number {
    const contractDetails = this.parseJsonField(row?.contract_details);
    return Number(contractDetails?.financed_amount ?? row?.financed_amount ?? 0);
  }

  getLendeeContractRecurringAmount(row: any): number {
    const contractDetails = this.parseJsonField(row?.contract_details);
    return Number(contractDetails?.recurring_amount ?? row?.contract_recurring_amount ?? row?.recurring_amount ?? 0);
  }

  getLendeeTerm(row: any): string {
    const contractDetails = this.parseJsonField(row?.contract_details);
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const term = contractDetails?.term ?? supporterDetails?.term ?? row?.lup_term ?? row?.term
      ?? row?.offer_duration_months ?? contractDetails?.offer_duration_months ?? supporterDetails?.offer_duration_months;
    return term != null ? String(term) : '-';
  }

  getLendeeSupporterInvestment(row: any): number {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    return Number(supporterDetails?.supporter_invest ?? supporterDetails?.supporter_investment ?? row?.supporter_invest ?? row?.supporter_investment ?? 0);
  }

  getLendeeSupporterReturn(row: any): number {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const supporterInsights = this.parseJsonField(row?.supporter_insights);
    return Number(supporterDetails?.supporter_return_amount ?? supporterInsights?.supporter_return ?? row?.supporter_return_amount ?? row?.supporter_return ?? 0);
  }

  getLendeeSupporterRecurringAmount(row: any): number {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    return Number(supporterDetails?.recurring_amount ?? row?.supporter_recurring_amount ?? 0);
  }

  getLendeeLendeeFees(row: any): string {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const contractDetails = this.parseJsonField(row?.contract_details);
    const fees = row?.lendee_profit_share_percent
      ?? supporterDetails?.lendee_profit_share_percent
      ?? supporterDetails?.lendee_fees
      ?? supporterDetails?.lendee_fee_percent
      ?? supporterDetails?.profit_percentage
      ?? supporterDetails?.lendee_profit_percentage
      ?? contractDetails?.lendee_fees
      ?? row?.lendee_fees
      ?? row?.lendee_fee_percent
      ?? row?.profit_percentage;
    if (fees == null || fees === '') return '-';
    const raw = String(fees);
    if (raw.includes('%')) return raw;
    const num = Number(fees);
    if (Number.isNaN(num)) return raw;
    if (num > 0 && num <= 1) return `${Math.round(num * 100)}%`;
    return `${num}%`;
  }

  getLendeeRequestId(row: any): string {
    const supporterDetails = this.parseJsonField(row?.supporter_details);
    const requestId = supporterDetails?.request_id ?? row?.lendee_request_id ?? row?.request_id;
    return requestId != null ? String(requestId) : '-';
  }

  getLendeeRemainingPayments(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    const contractDetails = this.parseJsonField(row?.contract_details);
    return Number(row?.remaining_payments ?? paymentDetails?.remaining_payments ?? contractDetails?.remaining_payments ?? 0);
  }

  getLendeePaidToBusiness(row: any): number {
    const paymentDetails = this.parseJsonField(row?.payment_details);
    return Number(paymentDetails?.total_paid_to_business ?? paymentDetails?.paid_payments ?? row?.total_paid_to_business ?? row?.paid_payments ?? row?.paid_to_business ?? 0);
  }

  private parseJsonField(value: any): any {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  getLendeePaymentTypeName(row: any): string {
    const type = row?.lendee_payment_type;
    if (type === 1) return 'Recurring Payment';
    if (type === 3) return 'Early Payout';
    if (type === 5) return 'Upfront Financing';
    return 'Payment';
  }

  getLendeeAmountDeducted(row: any): number {
    return Number(row?.recurring_amount ?? row?.payout_amount ?? 0);
  }

  showLendeeViewLogs(row: any): void {
    this.unpaidPaymentsModal.openNotes({
      title: 'Activity Logs',
      entries: [{
        userName: '',
        timestamp: '',
        action: '',
        note: 'No detailed logs available for this record.',
        sortKey: '',
      }],
      emptyMessage: 'No activity logs found.',
    });
  }

  onLendeeShareTypeChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    const next = Number(raw);
    if (this.lendeeShareTypeDraft() === next) return;
    this.lendeeShareTypeDraft.set(next);
  }

  onLendeePaymentStatusChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    const next = Number(raw);
    if (this.lendeePaymentStatusDraft() === next) return;
    this.lendeePaymentStatusDraft.set(next);
  }

  private buildListFilters(overrides: Partial<UnifiedUnpaidFilters> = {}): UnifiedUnpaidFilters {
    return {
      search_string: this.search(),
      limit: this.pageSize,
      offset: this.offset(),
      fm_filter: this.fmFilter(),
      fm_status_filter: this.fmStatusFilter(),
      fm_partner_status: this.fmPartnerStatus(),
      fm_orp_filter: this.orpFilter(),
      fm_instant_claim: this.instantClaim(),
      challenge_type: this.challengeType(),
      is_vip: this.vipType(),
      is_urgent: this.urgentOnly() ? '1' : '0',
      lendee_share_type: this.lendeeShareType(),
      lendee_payment_status: this.lendeePaymentStatus(),
      ...overrides,
    };
  }

  private getProduct(): UnifiedProductOption | undefined {
    return this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
  }

  private getProductConfig(product: UnifiedProductOption) {
    const config = this.productConfigService.getProductConfig(product.serverName);
    return config?.productServerName ? config : null;
  }

  private coerceDropdownPrimitive(value: unknown): string | number | null {
    if (value === undefined || value === null) return null;
    const single = Array.isArray(value) ? value[0] : value;
    if (single === undefined || single === null) return null;
    return single as string | number;
  }

  private readRouteStateFromSignals(): FmListRouteState {
    return {
      filter: this.fmFilter(),
      status: this.fmStatusFilter(),
      partnerStatus: this.fmPartnerStatus(),
      challenge: this.challengeType(),
      orp: this.orpFilter(),
      instant: this.instantClaim(),
      vip: this.vipType(),
      urgent: this.urgentOnly(),
      search: this.search(),
      page: this.page(),
      lendeeShareType: this.lendeeShareType(),
      lendeePaymentStatus: this.lendeePaymentStatus(),
      isDummy: this.isDummyService.isDummy(),
    };
  }

  private applyRouteState(state: FmListRouteState): void {
    this.isDummyService.syncFromRoute(state.isDummy);
    this.fmFilter.set(state.filter);
    this.fmStatusFilter.set(state.status);
    this.fmPartnerStatus.set(state.partnerStatus);
    this.challengeType.set(state.challenge);
    this.orpFilter.set(state.orp);
    this.instantClaim.set(state.instant);
    this.vipType.set(state.vip);
    this.urgentOnly.set(state.urgent);
    this.search.set(state.search);
    this.searchApplied.set(!!state.search.trim());
    this.page.set(state.page);
    if (state.lendeeShareType !== undefined) {
      this.lendeeShareType.set(state.lendeeShareType);
    }
    if (state.lendeePaymentStatus !== undefined) {
      this.lendeePaymentStatus.set(state.lendeePaymentStatus);
    }
    this.syncLendeeFilterDrafts();
    this.clearRowSelection();
    this.rowMenuOptionsCache.clear();
  }

  private navigateWithFilters(patch: Partial<FmListRouteState>): void {
    const next: FmListRouteState = { ...this.readRouteStateFromSignals(), ...patch };
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildFmListQueryParams(next, this.fmRouteDefaults),
      replaceUrl: true,
    });
  }

  private applyLendeeFilters(): void {
    this.navigateWithFilters({
      search: this.searchDraft(),
      page: 1,
      lendeeShareType: this.lendeeShareTypeDraft(),
      lendeePaymentStatus: this.lendeePaymentStatusDraft(),
    });
  }

  private syncLendeeFilterDrafts(): void {
    if (!this.isLendeeFilter()) return;
    this.lendeeShareTypeDraft.set(this.lendeeShareType());
    this.lendeePaymentStatusDraft.set(this.lendeePaymentStatus());
    this.searchDraft.set(this.search());
  }

  private resetLendeeFilters(): void {
    this.navigateWithFilters({
      search: '',
      page: 1,
      lendeeShareType: 0,
      lendeePaymentStatus: 0,
    });
  }
}
